import React, { useReducer, useEffect, useContext } from 'react';
import {
    makeStyles, useTheme,
    useMediaQuery, RadioGroup,
    FormControlLabel, Radio, Typography,
} from '@material-ui/core';
import Table from 'components/widgets/Table';
import ModalUtils from 'utils/ModalUtils';
import Header from 'components/widgets/Header';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';
import Container from 'components/widgets/Container';
import clsx from 'clsx';
import { useQuery, useSubscription } from '@apollo/client';
import NumberUtils from 'lib/NumberUtils';
import { DataSort, FetchPolicy } from 'utils/enum/Core';
import InputSearch from 'components/widgets/InputSearch';
import PartsQuery from 'services/graphQL/query/parts/PartsQuery';
import SubscriptionActionType from 'utils/enum/SubscriptionActionType';
import PartsSubscription from 'services/graphQL/subscription/parts/PartsSubscription';
import UserContext from 'components/context/UserContext';
import Filter from 'components/widgets/Filter';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

const useStyle = makeStyles((theme) => AccountingStyles.mainList(theme));
const ownStyle = makeStyles((theme) => ({
    highlightText: {
        color: theme.palette.text.red,
    },
    paddingTop20: {
        paddingTop: '20px',
    },
    filterWidth: {
        width: '170px',
        border: `solid 1px ${theme.palette.border.ghost}`,
        padding: '4px 5px',
        borderRadius: '0.2rem',
        paddingRight: '10px',
        '& div': {
            fontSize: '14px',
            fontWeight: 400,
        },
    },
    iconFilterCls: {
        position: 'relative',
        right: '25px',
        top: '6px',
        zIndex: '-1',
    },
}));

const ACTION_TYPES = {
    SET_EDITING: 'setEditing',
    SET_NEW: 'setNew',
    LOAD_MORE: 'setLoadMore',
    SET_PARAMS: 'setParams',
    SET_TABLE: 'setTable',
    SET_SELECTED_ROW: 'setSelectedRow',
    CLOSE_SCREEN: 'setCloseScreen',
    SET_INITIAL_STATE: 'setInitialState',
    SET_STATE_VALUES: 'setStateValues',
    SET_ACTION: 'setCBAction',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.SET_NEW: {
        return {
            ...state,
            selectedRow: null,
            isEditing: false,
        };
    }
    case ACTION_TYPES.SET_EDITING: {
        return {
            ...state,
            isEditing: true,
        };
    }
    case ACTION_TYPES.SET_SELECTED_ROW: {
        return {
            ...state,
            selectedRow: action.value,
        };
    }
    case ACTION_TYPES.SET_TABLE: {
        return {
            ...state,
            table: action.value,
        };
    }
    case ACTION_TYPES.SET_PARAMS: {
        return {
            ...state,
            table: {
                records: [],
                totalCount: 0,
            },
            params: action.value,
        };
    }
    case ACTION_TYPES.LOAD_MORE: {
        return {
            ...state,
            params: action.value,
        };
    }
    case ACTION_TYPES.CLOSE_SCREEN: {
        return {
            ...state,
            selectedRow: null,
            isEditing: false,
        };
    }
    case ACTION_TYPES.SET_INITIAL_STATE: {
        return action.value;
    }
    case ACTION_TYPES.SET_STATE_VALUES: {
        return {
            ...state,
            ...action.value,
        };
    }
    case ACTION_TYPES.SET_ACTION: {
        return {
            ...state,
            ...action.value,
        };
    }
    default: return action.value;
    }
};

const ReorderPartsList = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const classes = {
        ...useStyle(),
        ...ownStyle(),
    };

    const { availableLots, defaultLotId, defaultLot } = useContext(UserContext);

    const initState = {
        params: {
            paginate: {
                init: 0,
                limit: 50,
            },
            status: 'active',
            search: '',
            showAllVendors: true,
            lotName: [{
                label: defaultLot,
                value: defaultLot,
            }],
        },
        table: {
            records: [],
            totalCount: 0,
        },
        actionRecord: null,
        actionType: '',
        quantityToTransfer: 0,
        lotTransferId: 0,
    };

    const [state, dispatch] = useReducer(reducer, initState);
    const {
        params, table, selectedRow, lotTransferId,
    } = state;

    if (defaultLotId > 0 && lotTransferId === 0) {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            value: {
                lotTransferId: defaultLotId,
                lotTransfer: defaultLot,
            },
        });
    }

    const {
        data, loading, error,
    } = useQuery(PartsQuery.GET_PARTS_INVENTORY_LIST, {
        variables: {
            paginate: params.paginate,
            filter: {
                search: params.search,
                status: params.status,
                vendorId: 0,
                showAllVendors: params.showAllVendors,
                lotsName: params.lotName.map((item) => item.value),
                reorderParts: true,
            },
            sort: [
                { fieldName: 'description', dir: DataSort.ASC },
            ],
        },
        notifyOnNetworkStatusChange: false,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const onFilterChange = (param, value) => {
        if (params[param] === value) return;

        dispatch({
            type: ACTION_TYPES.SET_PARAMS,
            value: {
                ...params,
                [param]: value,
                paginate: {
                    ...params.paginate,
                    init: 0,
                },
            },
        });
    };

    const onSearch = (text) => {
        dispatch({
            type: ACTION_TYPES.SET_PARAMS,
            value: {
                ...params,
                search: text,
                paginate: {
                    ...params.paginate,
                    init: 0,
                },
            },
        });
    };

    const loadMore = () => {
        const currentOffset = table?.records?.length || 0;
        const { paginate } = params;

        dispatch({
            type: ACTION_TYPES.LOAD_MORE,
            value: {
                ...params,
                paginate: {
                    ...paginate,
                    init: currentOffset,
                },
            },
        });
    };

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading) {
            const { records } = table;
            const { getPartsInventoryList } = data;

            records.push(...getPartsInventoryList.data);

            dispatch({
                type: ACTION_TYPES.SET_TABLE,
                value: {
                    records,
                    totalCount: getPartsInventoryList.totalCount,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error]);

    useSubscription(PartsSubscription.PARTS_INVENTORY_SUBSCRIPTION, {
        onSubscriptionData: ({ subscriptionData }) => {
            const { action: actionSubs, data: subsData, id } = subscriptionData?.data?.partsInventoryChanged;

            if (actionSubs === SubscriptionActionType.ADDED) {
                const { records, totalCount } = table;
                const currentData = JSON.parse(subsData);

                records.unshift({ ...currentData });

                dispatch({
                    type: ACTION_TYPES.SET_TABLE,
                    value: {
                        records,
                        totalCount: totalCount + 1,
                    },
                });
            } else if (actionSubs === SubscriptionActionType.UPDATED) {
                const currentRecords = [...table.records];
                const currentData = JSON.parse(subsData);

                const currentIndex = currentRecords.findIndex((item) => Number(item.partsInventoryId) === Number(id));

                if (currentIndex >= 0) {
                    currentRecords.splice(currentIndex, 1);
                    currentRecords.splice(currentIndex, 0, { ...currentData });

                    dispatch({
                        type: ACTION_TYPES.SET_TABLE,
                        value: {
                            records: currentRecords,
                            totalCount: table.totalCount,
                        },
                    });
                }
            }
        },
    });

    const columns = [
        {
            minWidth: 30,
            width: 30,
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
            Cell: (cell) => cell.viewIndex + 1,
        },
        {
            Header: 'Stock Number',
            minWidth: 120,
            width: 120,
            id: 'PartStockNumber',
            accessor: 'partStockNumber',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
        },
        {
            Header: 'Description',
            minWidth: 200,
            width: 200,
            id: 'Description',
            accessor: 'description',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
        },
        {
            Header: 'Part Cost',
            minWidth: 100,
            width: 100,
            id: 'PartCost',
            accessor: 'partCost',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
            className: clsx(classes.columnStyle, classes.columnRight),
            Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
        },
        {
            Header: 'List Price',
            minWidth: 100,
            width: 100,
            id: 'NetPrice',
            accessor: 'netPrice',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
            className: clsx(classes.columnStyle, classes.columnRight),
            Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
        },
        {
            Header: 'QOH',
            minWidth: 70,
            width: 70,
            id: 'QuantityOnHand',
            accessor: 'quantityOnHand',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
            className: clsx(classes.columnStyle, classes.columnRight),
        },
        {
            Header: 'Reorder Qty',
            minWidth: 90,
            width: 90,
            id: 'reorderQuantity',
            accessor: 'reorderQuantity',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
            className: clsx(classes.columnStyle, classes.columnRight),
        },
        {
            Header: 'Lot',
            minWidth: 120,
            width: 120,
            id: 'LotName',
            accessor: 'lotName',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
        },
        {
            Header: 'Bin',
            minWidth: 120,
            width: 120,
            id: 'binNumber',
            accessor: 'binNumber',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
        },
        {
            Header: 'Shelf',
            minWidth: 120,
            width: 120,
            id: 'shelf',
            accessor: 'shelf',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
        },
        {
            Header: 'Notes',
            minWidth: 90,
            id: 'QuickNote',
            accessor: 'quickNote',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
        },
    ];

    return (
        <div className={classes.main}>
            <Header>
                <div className={classes.containerFilterSearch}>
                    <InputSearch
                        customClasses={clsx(classes.search, isMobile ? classes.searchFull : '')}
                        initialSearch={params.search || ''}
                        onSearch={onSearch}
                    />
                    <div className="d-flex-align-baseline-space-between">
                        <Typography
                            variant="h5"
                            className={classes.filterLabel}
                        >
                            Lot: &nbsp;
                        </Typography>
                        <Filter
                            showTooltip
                            useInternalSearch
                            emptyRecord="All"
                            maxWidthLabel={150}
                            className={classes.filterWidth}
                            records={availableLots}
                            selectedValues={params.lotName || []}
                            applyFilter={(record) => onFilterChange('lotName', record)}
                            onClearFilter={() => onFilterChange('lotName', [])}
                        />
                        <ArrowDropDownIcon className={classes.iconFilterCls} />
                    </div>
                    <RadioGroup row value={params.status} onChange={(e, val) => onFilterChange('status', val)}>
                        <FormControlLabel
                            value="active"
                            control={<Radio color="primary" />}
                            label="Active Parts"
                        />
                        <FormControlLabel
                            value="inactive"
                            control={<Radio color="primary" />}
                            label="In-Active Parts"
                        />
                    </RadioGroup>
                </div>
            </Header>
            <Container className={classes.containerSplit}>
                <Table
                    data={table?.records}
                    columns={columns}
                    cursor="default"
                    load={loading}
                    totalRecords={table?.totalCount}
                    loadMore={loadMore}
                    rowSelected
                    enableRowHover
                    className="-highlight actionsInLine"
                    getTrProps={(_, rowInfo) => ({
                        onClick: () => {
                            if (selectedRow?.partsInventoryId === rowInfo.original.partsInventoryId) return;
                            dispatch({ type: ACTION_TYPES.SET_SELECTED_ROW, value: rowInfo.original });
                        },
                    })}
                />
            </Container>
        </div>
    );
};

export default ReorderPartsList;
